import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { resolveOrgTokens } from "@/lib/resolveOrgTokens";
import { useDisplayName } from "@/hooks/useDisplayName";
import { QaToolkitPanel } from "@/components/qa/QaToolkitPanel";

export default function SubmissionViewer() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const displayName = useDisplayName();
  const [header, setHeader] = useState<any | null>(null);
  const [qna, setQna] = useState<any[]>([]);
  const [evidence, setEvidence] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        if (!assessmentId) return;
        setLoading(true);

        // 1) Fetch the assessment (no join)
        const { data: a, error: aErr } = await supabase
          .from("assessments")
          .select("id, org_id, status, submitted_at, created_at")
          .eq("id", assessmentId)
          .single();
        if (aErr) throw aErr;
        
        let orgName = 'the portfolio';
        
        // 2) Fetch the portfolio name from portfolios table if we have org_id
        if (a?.org_id) {
          const { data: p, error: pErr } = await (supabase as any)
            .from("portfolios")
            .select("name")
            .eq("id", a.org_id)
            .single();
          if (!pErr && p) {
            orgName = p.name;
          }
        }
        
        setHeader({ ...a, org_name: orgName });

        // 2) Q&A (respondent answers)
        const { data: qnaData, error: qErr } = await supabase
          .from("v_reviewer_qna") // your existing view that joins questions & responses
          .select("question_id, question_text, domain, respondent_score, respondent_comment, evidence_count")
          .eq("assessment_id", assessmentId)
          .order("domain", { ascending: true });
        if (qErr) throw qErr;
        setQna(qnaData ?? []);

        // 3) Evidence list via secure RPC
        const { data: ev, error: evErr } = await (supabase.rpc as any)(
          'get_assessment_evidence',
          { p_assessment_id: assessmentId }
        );
        if (evErr) throw evErr;
        const mappedEv = (ev ?? []).map((e: any) => ({
          ...e,
          file_path: e.file_url || e.link_url || '',
          uploaded_at: e.created_at,
        }));
        setEvidence(mappedEv);
      } catch (e) {
        console.error("[SubmissionViewer] load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [assessmentId]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Submitted Assessment</h1>
          {header && (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground/80">{header.org_name}</span>
              {displayName && (
                <>
                  <span className="mx-2 text-border">|</span>
                  <span>{displayName}</span>
                </>
              )}
              <span className="mx-2">•</span>
              Status: {header.status}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
      </div>

      {!loading && header && assessmentId && (
        <div className="print:hidden">
          <QaToolkitPanel assessmentId={assessmentId} />
        </div>
      )}

      {loading && <p>Loading…</p>}

      {!loading && header && (
        <>
          <section className="space-y-2">
            <h2 className="font-semibold">Responses</h2>
            {qna.length === 0 && <p className="text-sm text-muted-foreground">No answers found.</p>}
            <div className="space-y-3">
              {qna.map((row) => (
                <div key={row.question_id} className="rounded-md border p-3">
                  <div className="text-xs uppercase text-muted-foreground">{row.domain}</div>
                  <div className="font-medium">{resolveOrgTokens(row.question_text, header?.org_name)}</div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">Score:</span> {row.respondent_score ?? "—"}
                  </div>
                  {row.respondent_comment && (
                    <div className="text-sm mt-1">
                      <span className="font-medium">Comment:</span> {row.respondent_comment}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    Evidence items: {row.evidence_count ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </section>

            <section className="space-y-2">
              <h2 className="font-semibold">Evidence</h2>
              {evidence.length === 0 && (
                <p className="text-sm text-muted-foreground">No evidence uploaded.</p>
              )}
              <div className="space-y-2">
                {evidence.map((e) => (
                  <div key={e.id} className="flex items-center justify-between rounded border p-2">
                    <div className="space-y-0.5">
                      <div className="text-xs uppercase text-muted-foreground">{e.domain}</div>
                      <div className="text-sm">{e.file_path}</div>
                      <div className="text-xs text-muted-foreground">
                        Uploaded: {new Date(e.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                    {/* If you have signed URL download, you can add a Download button here later */}
                  </div>
                ))}
              </div>
            </section>
        </>
      )}
    </div>
  );
}